import type { Context as HonoContext } from 'hono'
import { buildUserKey, extractIp } from './helpers'
import { createRateLimiter } from './middleware'
import type { HonoRateLimiterInstance } from './middleware'
import type { RateLimitOptions } from '../../types'

// ── Strategy aliases ───────────────────────────────────────────────────────────

export const createFixedRateLimiter = (
  options: Omit<RateLimitOptions<HonoContext>, 'strategy'> = {}
): HonoRateLimiterInstance =>
  createRateLimiter({ ...options, strategy: 'fixed' })

export const createSlidingRateLimiter = (
  options: Omit<RateLimitOptions<HonoContext>, 'strategy'> = {}
): HonoRateLimiterInstance =>
  createRateLimiter({ ...options, strategy: 'sliding' })

// ── User-scoped factories ──────────────────────────────────────────────────────

/**
 * Rate limiter keyed by a user ID stored in Hono context variables via
 * `c.set('user', { id: '...' })`, falling back to the client IP.
 */
export const createUserRateLimiter = (
  options: RateLimitOptions<HonoContext> = {}
): HonoRateLimiterInstance =>
  createRateLimiter({ ...options, keyGenerator: buildUserKey })

export const createSlidingUserRateLimiter = (
  options: Omit<RateLimitOptions<HonoContext>, 'strategy'> = {}
): HonoRateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: buildUserKey
  })

// ── Endpoint-scoped factories ──────────────────────────────────────────────────

export const createEndpointRateLimiter = (
  endpoint: string,
  options: RateLimitOptions<HonoContext> = {}
): HonoRateLimiterInstance =>
  createRateLimiter({
    ...options,
    keyGenerator: (c: HonoContext) => {
      const base = options.keyGenerator
        ? options.keyGenerator(c)
        : extractIp(c)
      return `${endpoint}:${base}`
    }
  })

export const createSlidingEndpointRateLimiter = (
  endpoint: string,
  options: Omit<RateLimitOptions<HonoContext>, 'strategy'> = {}
): HonoRateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: (c: HonoContext) => {
      const base = options.keyGenerator
        ? options.keyGenerator(c)
        : extractIp(c)
      return `${endpoint}:${base}`
    }
  })
